import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, RefreshCw } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from "recharts";
import { buildItemCodeMap, calcFreebieKpis, isFreebieLine } from "@/lib/freebieCalc";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import QuotationTypeToggle from "@/components/QuotationTypeToggle";
import { useQuotationTypeView, matchesQuotationType } from "@/hooks/useQuotationTypeView";

const ALL_BRANDS = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];
const CARD_COLORS = { amber: "#d97706", coral: "#ef4444" };
const formatCurrency = (v) => { if (v >= 1e7) return `₹${(v/1e7).toFixed(2)} Cr`; if (v >= 1e5) return `₹${(v/1e5).toFixed(2)} L`; if (v >= 1e3) return `₹${(v/1e3).toFixed(1)} K`; return `₹${v.toLocaleString("en-IN")}`; };

export default function FreebieAnalytics() {
  const [allQuotations, setAllQuotations] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const today = now.toISOString().substring(0,10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandFilterOpen, setBrandFilterOpen] = useState(false);
  const [typeView] = useQuotationTypeView();

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, sRes, pRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/sellers`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true })
      ]);
      setAllQuotations(qRes.data || []);
      setAllSellers(sRes.data || []);
      setAllProducts(pRes.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const sellerMap = useMemo(() => { const m = {}; allSellers.forEach(s => { m[s.id] = s; }); return m; }, [allSellers]);
  const itemCodeMap = useMemo(() => buildItemCodeMap(allProducts), [allProducts]);
  const toggleBrand = (b) => setSelectedBrands(p => p.includes(b) ? p.filter(x=>x!==b) : [...p, b]);
  const clearFilters = () => { setDateFrom(monthStart); setDateTo(today); setSelectedBrands([]); };
  const hasActiveFilters = dateFrom || dateTo || selectedBrands.length > 0;

  const filteredQuotations = useMemo(() => {
    let f = [...allQuotations];
    f = f.filter(q => !!q.invoice_date);
    if (typeView !== "all") f = f.filter(q => matchesQuotationType(q, typeView));
    if (dateFrom) f = f.filter(q => (q.invoice_date?.substring(0,10)||"") >= dateFrom);
    if (dateTo) f = f.filter(q => (q.invoice_date?.substring(0,10)||"") <= dateTo);
    if (selectedBrands.length > 0) f = f.filter(q => [...(q.items||[]),...(q.freebies||[])].some(i => selectedBrands.includes(i.brand)));
    return f;
  }, [allQuotations, dateFrom, dateTo, selectedBrands, typeView]);

  const kpi = useMemo(() => calcFreebieKpis(filteredQuotations, itemCodeMap), [filteredQuotations, itemCodeMap]);

  const freebieByDate = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const d = q.invoice_date?.substring(0,10);
      if (!d) return;
      if (!m[d]) m[d] = { freebVal: 0, rev: 0, allLines: 0 };
      const allLines = [...(q.items || []), ...(q.freebies || [])];
      for (const ln of allLines) {
        const val = (Number(ln.mrp) || 0) * (Number(ln.quantity) || 0);
        m[d].allLines += val;
        if (isFreebieLine(ln, itemCodeMap)) m[d].freebVal += val;
      }
      m[d].rev += q.total_invoice_value || 0;
    });
    return Object.entries(m).sort(([a],[b]) => a.localeCompare(b)).map(([d,v]) => ({
      date: d,
      freebieValue: v.freebVal,
      revenue: v.rev,
      freebiePct: v.allLines > 0 ? (v.freebVal / v.allLines) * 100 : 0,
      label: new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
    }));
  }, [filteredQuotations, itemCodeMap]);

  const topFreebieSkus = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const allLines = [...(q.items || []), ...(q.freebies || [])];
      for (const ln of allLines) {
        if (!isFreebieLine(ln, itemCodeMap)) continue;
        const k = ln.sku_id || ln.product_name;
        if (!m[k]) m[k] = { sku_id: ln.sku_id, product_name: ln.product_name, brand: ln.brand, qty: 0, value: 0 };
        m[k].qty += ln.quantity || 0;
        m[k].value += (Number(ln.mrp) || 0) * (Number(ln.quantity) || 0);
      }
    });
    return Object.values(m).sort((a,b) => b.value - a.value).slice(0, 15);
  }, [filteredQuotations, itemCodeMap]);

  const freebiesBySeller = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const allLines = [...(q.items || []), ...(q.freebies || [])];
      let freebVal = 0;
      for (const ln of allLines) {
        if (isFreebieLine(ln, itemCodeMap)) freebVal += (Number(ln.mrp) || 0) * (Number(ln.quantity) || 0);
      }
      if (freebVal <= 0) return;
      const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (!m[name]) m[name] = { name, value: 0, count: 0 };
      m[name].value += freebVal;
      m[name].count++;
    });
    return Object.values(m).sort((a,b) => b.value - a.value).slice(0, 15);
  }, [filteredQuotations, itemCodeMap, sellerMap]);

  const FilterCheckItem = ({ checked, label, onClick }) => (
    <div className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${checked ? 'bg-accent/10' : ''}`} onClick={onClick}>
      <div className={`w-4 h-4 rounded border ${checked ? 'bg-accent border-accent' : 'border-slate-300'}`}>{checked && <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
      <span className="text-sm">{label}</span>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" /></div>;

  return (
    <div className="space-y-5">
      <div className="bg-white pb-4 mb-1 border-b border-[#E8E8E8]">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-[#0F0F0F] mr-auto">Freebie Analytics</h1>
          <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f,t)=>{ setDateFrom(f); setDateTo(t); }} testId="freebie-date-range" />
          <QuotationTypeToggle testId="freebie-qtype" />
          <Popover open={brandFilterOpen} onOpenChange={setBrandFilterOpen}>
            <PopoverTrigger asChild><Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">{selectedBrands.length===0?"All Brands":`${selectedBrands.length} Brand${selectedBrands.length>1?"s":""}`}</Button></PopoverTrigger>
            <PopoverContent className="w-56 p-2"><div className="space-y-1">{ALL_BRANDS.map(b=><FilterCheckItem key={b} checked={selectedBrands.includes(b)} label={b} onClick={()=>toggleBrand(b)} />)}</div></PopoverContent>
          </Popover>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs text-red-500 hover:bg-red-50"><X className="h-3 w-3 mr-1" />Clear</Button>}
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8 px-2 border-[#E8E8E8]"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-[#E8E8E8]"><CardContent className="pt-5 text-center"><p className="text-xs text-[#1A1A1A]/50 mb-1">Total Freebie Value</p><p className="text-2xl font-bold font-mono" style={{color:CARD_COLORS.coral}}>{formatCurrency(kpi.totalFreebieValue)}</p></CardContent></Card>
        <Card className="border-[#E8E8E8]"><CardContent className="pt-5 text-center"><p className="text-xs text-[#1A1A1A]/50 mb-1">Avg. Freebie %</p><p className="text-2xl font-bold font-mono" style={{color:CARD_COLORS.amber}}>{kpi.avgFreebiePct.toFixed(2)}%</p></CardContent></Card>
      </div>

      <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Freebie Value Trend</CardTitle></CardHeader>
        <CardContent><div className="h-[260px]"><ResponsiveContainer width="100%" height="100%"><AreaChart data={freebieByDate} margin={{top:5,right:20,left:0,bottom:5}}><defs><linearGradient id="fg2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.15}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis dataKey="label" tick={{fontSize:10}} tickLine={false} axisLine={{stroke:"#E8E8E8"}} interval={freebieByDate.length>15?Math.floor(freebieByDate.length/8):0}/><YAxis tickFormatter={formatCurrency} tick={{fontSize:10}} tickLine={false} axisLine={false} width={70}/><Tooltip formatter={v=>[`₹${v.toLocaleString("en-IN")}`,"Freebie Value"]} contentStyle={{borderRadius:8,border:"1px solid #E8E8E8",fontSize:12}}/><Area type="monotone" dataKey="freebieValue" stroke="#ef4444" strokeWidth={2} fill="url(#fg2)"/></AreaChart></ResponsiveContainer></div></CardContent>
      </Card>

      <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Freebie % vs Revenue</CardTitle><p className="text-xs text-muted-foreground">Dual axis comparison</p></CardHeader>
        <CardContent><div className="h-[260px]"><ResponsiveContainer width="100%" height="100%"><LineChart data={freebieByDate} margin={{top:5,right:20,left:0,bottom:5}}><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis dataKey="label" tick={{fontSize:10}} tickLine={false} axisLine={{stroke:"#E8E8E8"}} interval={freebieByDate.length>15?Math.floor(freebieByDate.length/8):0}/><YAxis yAxisId="left" tickFormatter={formatCurrency} tick={{fontSize:10}} tickLine={false} axisLine={false} width={70}/><YAxis yAxisId="right" orientation="right" tickFormatter={v=>`${v.toFixed(0)}%`} tick={{fontSize:10}} tickLine={false} axisLine={false} width={40}/><Tooltip formatter={(v,n)=>[n==="revenue"?formatCurrency(v):`${Number(v).toFixed(2)}%`,n==="revenue"?"Revenue":"Freebie %"]} contentStyle={{borderRadius:8,fontSize:12}}/><Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#1D9E75" strokeWidth={2} dot={false}/><Line yAxisId="right" type="monotone" dataKey="freebiePct" stroke="#d97706" strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer></div></CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Top Freebie SKUs</CardTitle></CardHeader>
          <CardContent><div className="max-h-[350px] overflow-auto"><Table><TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs">#</TableHead><TableHead className="text-xs">SKU Name</TableHead><TableHead className="text-xs">SKU ID</TableHead><TableHead className="text-xs text-right">Qty</TableHead><TableHead className="text-xs text-right">Value</TableHead></TableRow></TableHeader><TableBody>
            {topFreebieSkus.map((s,i)=>(<TableRow key={s.sku_id||i}><TableCell className="text-xs">{i+1}</TableCell><TableCell className="text-xs">{s.product_name||"-"}</TableCell><TableCell className="text-xs font-mono">{s.sku_id||"-"}</TableCell><TableCell className="text-xs text-right">{s.qty}</TableCell><TableCell className="text-xs text-right font-mono">{formatCurrency(s.value)}</TableCell></TableRow>))}
          </TableBody></Table></div></CardContent>
        </Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Freebie by Seller</CardTitle></CardHeader>
          <CardContent><div className="max-h-[350px] overflow-auto"><Table><TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs">Seller</TableHead><TableHead className="text-xs text-right">Freebie Value</TableHead><TableHead className="text-xs text-right">Orders</TableHead></TableRow></TableHeader><TableBody>
            {freebiesBySeller.map((s,i)=>(<TableRow key={i}><TableCell className="text-xs">{s.name}</TableCell><TableCell className="text-xs text-right font-mono">{formatCurrency(s.value)}</TableCell><TableCell className="text-xs text-right">{s.count}</TableCell></TableRow>))}
          </TableBody></Table></div></CardContent>
        </Card>
      </div>
    </div>
  );
}
